import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  message,
  Progress,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import DashboardLayout from "../components/DashboardLayout";
import PageLoader from "../components/PageLoader";
import RewardedProfileDisplay from "../components/RewardedProfileDisplay";
import RewardRedeemModal from "../components/RewardRedeemModal";
import avatarImage from "../assets/spr_placeholder_avatar.png";
import useCurrentUserData from "../hooks/useCurrentUserData";
import useRewardsCatalog from "../hooks/useRewardsCatalog";
import { useAuth } from "../contexts/AuthContext";
import { UpdateUserRewardsState, UserRewardState } from "../firebase-config";
import { trackReward } from "../analytics";
import { dashboardPalette } from "../theme";
import {
  getRewardBadgeVisualValue,
  isImageSource,
  isRewardEquipable,
  mergeRewardsWithUserState,
  NormalizedReward,
  normalizeRewardCollection,
} from "../utils/rewards";
import {
  buildClaimState,
  buildEquipState,
  clearLegacyRewardStorage,
  readLegacyClaimedRewardIds,
} from "../utils/rewardState";
import { getExpProgress, getLevelFromExp } from "../utils/levels";

const getTrackProgress = (
  level: number,
  segmentProgress: number,
  rewardCount: number,
) => {
  if (rewardCount <= 1) return 100;
  return Math.min(
    100,
    ((level - 1 + segmentProgress / 100) / (rewardCount - 1)) * 100,
  );
};

const formatPercent = (value: number) =>
  Number.isFinite(value) ? value.toFixed(2) : "0.00";

const getRewardPalette = (reward: NormalizedReward) => {
  switch (reward.tone) {
    case "teal":
      return {
        background: "linear-gradient(180deg, #ffe6b2 0%, #ffcf83 100%)",
        border: "#f1ba56",
        badgeBackground: "#b9eef2",
        badgeColor: "#2a8791",
        textColor: dashboardPalette.headerBlue,
      };
    case "sunset":
      return {
        background: "linear-gradient(180deg, #ffbb3b 0%, #ffce56 100%)",
        border: "#ec9d00",
        badgeBackground: "#ffd86b",
        badgeColor: dashboardPalette.headerBlue,
        textColor: dashboardPalette.headerBlue,
      };
    case "sky":
      return {
        background: "linear-gradient(180deg, #73d0ff 0%, #56b9f1 100%)",
        border: "#2f9bdc",
        badgeBackground: "rgba(255, 255, 255, 0.72)",
        badgeColor: dashboardPalette.headerBlue,
        textColor: "#ffffff",
      };
    default:
      return {
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(240,242,248,0.92) 100%)",
        border: "#d9deeb",
        badgeBackground: "rgba(255, 255, 255, 0.85)",
        badgeColor: "#9aa5c2",
        textColor: dashboardPalette.headerBlue,
      };
  }
};

const getRewardActionLabel = (
  reward: NormalizedReward,
  locked: boolean,
  saving: boolean,
) => {
  if (saving) return "Guardando…";
  if (locked) return "Bloqueado";
  if (!reward.claimed) return "Reclamar";
  if (reward.equipped) return "Equipado";
  if (isRewardEquipable(reward)) return "Equipar";
  return "Obtenido";
};

const Redeem: React.FC = () => {
  const { userData, isLoading: userLoading } = useCurrentUserData();
  const { catalog, isLoading: catalogLoading } = useRewardsCatalog();
  const { user } = useAuth();
  const [redeemInfoReward, setRedeemInfoReward] =
    useState<NormalizedReward | null>(null);
  const [isProgressInfoOpen, setIsProgressInfoOpen] = useState(false);
  const [savingRewardId, setSavingRewardId] = useState<string | null>(null);
  const legacyMigrationDone = useRef(false);

  const mergedRewards = useMemo(
    () => mergeRewardsWithUserState(catalog, userData?.Recompensas),
    [catalog, userData?.Recompensas],
  );

  const battlePassRewards = useMemo(
    () =>
      [...mergedRewards]
        .sort((a, b) => (a.level || 0) - (b.level || 0))
        .map((reward, index) => ({
          ...reward,
          level: reward.level || index + 1,
        })),
    [mergedRewards],
  );

  const extraInventoryItems = useMemo(
    () =>
      normalizeRewardCollection(userData?.Items).filter(
        (reward) => reward.type !== "placeholder",
      ),
    [userData?.Items],
  );

  const claimedBattlePassRewards = useMemo(
    () =>
      battlePassRewards.filter(
        (reward) => reward.claimed && reward.type !== "placeholder",
      ),
    [battlePassRewards],
  );

  const inventoryItems = useMemo(
    () => [...claimedBattlePassRewards, ...extraInventoryItems],
    [claimedBattlePassRewards, extraInventoryItems],
  );

  const currentExp = userData?.CurrentExp ?? 0;
  const derivedLevel = getLevelFromExp(currentExp);
  const currentLevel = Math.min(derivedLevel, battlePassRewards.length || 1);
  const currentExpProgress = getExpProgress(derivedLevel, currentExp);
  const trackProgress = getTrackProgress(
    currentLevel,
    currentExpProgress,
    battlePassRewards.length,
  );

  const battlePassScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = battlePassScrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(
      `[data-reward-level="${currentLevel}"]`,
    );
    target?.scrollIntoView({ inline: "start", block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLevel, battlePassRewards.length]);

  const scrollBattlePass = (direction: 1 | -1) => {
    const container = battlePassScrollRef.current;
    if (!container) return;
    container.scrollBy({
      left: container.clientWidth * direction,
      behavior: "smooth",
    });
  };

  const saveRewardState = async (
    nextState: UserRewardState[],
    reward: NormalizedReward,
    track: boolean,
  ) => {
    if (!user) return;
    setSavingRewardId(reward.id);
    try {
      await UpdateUserRewardsState(user.uid, nextState);
      if (track) trackReward(reward.name);
    } catch {
      message.error(
        "No se pudo guardar el cambio. Revisá tu conexión y volvé a intentar.",
      );
    } finally {
      setSavingRewardId(null);
    }
  };

  const handleRewardAction = (reward: NormalizedReward) => {
    if (savingRewardId || reward.type === "placeholder") return;
    if (reward.level > currentLevel) return;

    if (!reward.claimed) {
      const nextState = buildClaimState(
        mergedRewards,
        [reward.id],
        isRewardEquipable(reward) ? reward.id : undefined,
      );
      void saveRewardState(nextState, reward, true);
      return;
    }

    if (isRewardEquipable(reward)) {
      const nextState = buildEquipState(
        mergedRewards,
        reward.id,
        !reward.equipped,
      );
      void saveRewardState(nextState, reward, !reward.equipped);
    }
  };

  useEffect(() => {
    if (legacyMigrationDone.current) return;
    if (!user || !userData || !mergedRewards.length) return;
    legacyMigrationDone.current = true;

    const legacyIds = readLegacyClaimedRewardIds();
    if (!legacyIds.length) return;

    const level = getLevelFromExp(userData.CurrentExp ?? 0);
    const pendingIds = mergedRewards
      .filter(
        (reward) =>
          legacyIds.includes(reward.id) &&
          !reward.claimed &&
          reward.type !== "placeholder" &&
          reward.level <= level,
      )
      .map((reward) => reward.id);

    if (!pendingIds.length) {
      clearLegacyRewardStorage();
      return;
    }

    UpdateUserRewardsState(user.uid, buildClaimState(mergedRewards, pendingIds))
      .then(clearLegacyRewardStorage)
      .catch((error) =>
        console.error("No se pudieron migrar los reclamos viejos:", error),
      );
  }, [user, userData, mergedRewards]);

  const renderRewardVisual = (reward: NormalizedReward, size: number) => {
    const visualValue = getRewardBadgeVisualValue(reward);
    if (!visualValue) return null;
    if (isImageSource(visualValue)) {
      return (
        <img
          src={visualValue}
          alt={reward.name}
          style={{ width: size, height: size, objectFit: "contain" }}
        />
      );
    }
    return (
      <span
        style={{
          fontSize: `${Math.max(22, size * 0.52)}px`,
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {visualValue}
      </span>
    );
  };

  const isPageLoading =
    (userLoading && !userData) || (catalogLoading && !catalog.length);

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1440, margin: "0 auto", width: "100%" }}>
        {isPageLoading ? (
          <PageLoader minHeight={480} />
        ) : (
          <>
            <Card
              className="app-glass hero-panel"
              style={{
                borderRadius: 24,
                border: "1px solid var(--dashboard-panel-border)",
                boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
              }}
              bodyStyle={{ padding: 24 }}
            >
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Typography.Text
                  strong
                  style={{
                    color: dashboardPalette.headerBlue,
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                  }}
                >
                  Recompensas
                </Typography.Text>
                <Typography.Title
                  level={2}
                  style={{ margin: 0, color: dashboardPalette.headerBlue }}
                >
                  Progreso y recompensas
                </Typography.Title>
                <Typography.Text style={{ color: dashboardPalette.textDark }}>
                  Reclamá lo que desbloqueaste y elegí qué cosmético mostrar en
                  tu perfil.
                </Typography.Text>
              </Space>
            </Card>

            <Card
              className="app-glass"
              style={{
                marginTop: 16,
                borderRadius: 24,
                border: "1px solid var(--dashboard-panel-border)",
                boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 16,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Typography.Title
                    level={4}
                    style={{ margin: 0, color: dashboardPalette.headerBlue }}
                  >
                    Tu avance
                  </Typography.Title>
                  <Button
                    icon={<InfoCircleOutlined />}
                    onClick={() => setIsProgressInfoOpen((prev) => !prev)}
                    style={{ borderRadius: 14, fontWeight: 800 }}
                  >
                    {isProgressInfoOpen ? "Ocultar ayuda" : "Ver ayuda"}
                  </Button>
                </div>

                <div
                  style={{
                    display: isProgressInfoOpen ? "block" : "none",
                    borderRadius: 18,
                    padding: 16,
                    background: "rgba(255,255,255,0.72)",
                    border: `1px solid ${dashboardPalette.panelBorder}`,
                  }}
                >
                  <Typography.Text style={{ color: dashboardPalette.textDark }}>
                    La barra refleja tu avance en el pase. Las recompensas de
                    niveles que todavía no alcanzaste muestran un candado. Tocá
                    una recompensa disponible para reclamarla. Los cosméticos
                    que ya obtuviste se pueden equipar o cambiar cuando quieras,
                    desde su tarjeta o desde el Inventario; si tocás el que
                    tenés equipado, te lo quitás.
                  </Typography.Text>
                </div>

                <Progress
                  percent={Math.max(0, Math.min(100, trackProgress))}
                  strokeColor="var(--dashboard-teal)"
                  trailColor="var(--dashboard-track)"
                  format={(percent) => `${Number(percent ?? 0).toFixed(2)}%`}
                />
                <Typography.Text
                  strong
                  style={{ color: dashboardPalette.headerBlue }}
                >
                  Nivel {currentLevel} de {battlePassRewards.length || 1}
                </Typography.Text>
                <Typography.Text style={{ color: dashboardPalette.textDark }}>
                  Progreso dentro del nivel: {formatPercent(currentExpProgress)}
                  %
                </Typography.Text>
              </Space>
            </Card>

            <Card
              className="app-glass"
              style={{
                marginTop: 16,
                borderRadius: 24,
                border: "1px solid var(--dashboard-panel-border)",
                boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Space direction="vertical" size={2}>
                  <Typography.Title
                    level={4}
                    style={{
                      margin: 0,
                      color: dashboardPalette.headerBlue,
                    }}
                  >
                    Pase de recompensas
                  </Typography.Title>
                  <Typography.Text style={{ color: dashboardPalette.textDark }}>
                    Deslizá para ver todos los niveles. Tocá un cosmético
                    obtenido para equiparlo.
                  </Typography.Text>
                </Space>

                <div
                  style={{
                    borderRadius: 18,
                    padding: "16px 12px 20px",
                    background: "rgba(255,255,255,0.72)",
                    border: `1px solid ${dashboardPalette.panelBorder}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Button
                    type="text"
                    shape="circle"
                    icon={<LeftOutlined />}
                    onClick={() => scrollBattlePass(-1)}
                    aria-label="Ver niveles anteriores"
                    style={{
                      flexShrink: 0,
                      color: dashboardPalette.headerBlue,
                      fontSize: 18,
                    }}
                  />
                  <div
                    ref={battlePassScrollRef}
                    className="battlepass-rail"
                    style={{
                      flex: 1,
                      display: "flex",
                      gap: 20,
                      overflowX: "auto",
                      scrollSnapType: "x mandatory",
                      padding: "4px 2px 12px",
                    }}
                  >
                    {battlePassRewards.map((reward) => {
                      const palette = getRewardPalette(reward);
                      const locked = reward.level > currentLevel;
                      const claimed = reward.claimed;
                      const equipped = !locked && reward.equipped;
                      const isSaving = savingRewardId === reward.id;
                      const isActionable =
                        !locked &&
                        reward.type !== "placeholder" &&
                        (!claimed || isRewardEquipable(reward));

                      return (
                        <div
                          key={reward.id}
                          data-reward-level={reward.level}
                          className="battlepass-card-slot"
                          style={{ scrollSnapAlign: "start" }}
                        >
                          <Card
                            hoverable={isActionable}
                            onClick={() =>
                              isActionable && handleRewardAction(reward)
                            }
                            style={{
                              width: "100%",
                              height: 300,
                              borderRadius: 18,
                              overflow: "hidden",
                              border: `1px solid ${palette.border}`,
                              background: palette.background,
                              boxShadow: equipped
                                ? "0 0 0 2px rgba(79,194,134,0.6) inset"
                                : "none",
                              cursor: isActionable ? "pointer" : "default",
                            }}
                            bodyStyle={{
                              padding: 14,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <Space
                              direction="vertical"
                              size={8}
                              style={{ width: "100%" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Tag color="gold">Nivel {reward.level}</Tag>
                                {locked ? (
                                  <Tag icon={<LockOutlined />}>Bloqueado</Tag>
                                ) : claimed ? (
                                  <Tag color="green">Obtenido</Tag>
                                ) : (
                                  <Tag color="blue">Disponible</Tag>
                                )}
                              </div>

                              <div
                                style={{
                                  minHeight: 64,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    width: 48,
                                    height: 48,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    opacity: locked ? 0.55 : 1,
                                  }}
                                >
                                  {renderRewardVisual(reward, 48)}
                                  {locked && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: -2,
                                        right: -2,
                                        width: 20,
                                        height: 20,
                                        borderRadius: "50%",
                                        background: "rgba(47,43,116,0.92)",
                                        border: "2px solid #fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <LockOutlined
                                        style={{ color: "#fff", fontSize: 10 }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>

                              <Typography.Title
                                level={5}
                                ellipsis
                                style={{
                                  margin: 0,
                                  fontSize: 15,
                                  color: palette.textColor,
                                }}
                              >
                                {reward.name}
                              </Typography.Title>
                              <Typography.Paragraph
                                ellipsis={{ rows: 2 }}
                                style={{
                                  margin: 0,
                                  fontSize: 12,
                                  color: palette.textColor,
                                  opacity: 0.9,
                                  minHeight: 34,
                                }}
                              >
                                {reward.description ||
                                  "Recompensa desbloqueable"}
                              </Typography.Paragraph>
                            </Space>

                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                              }}
                            >
                              {!locked && reward.redeemEnabled ? (
                                <Button
                                  block
                                  size="small"
                                  icon={<QuestionCircleOutlined />}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setRedeemInfoReward(reward);
                                  }}
                                  style={{
                                    height: 26,
                                    borderRadius: 12,
                                    fontWeight: 700,
                                    background: "rgba(255,255,255,0.55)",
                                    borderColor: palette.border,
                                    color: "var(--dashboard-header-blue)",
                                  }}
                                >
                                  Cómo canjear
                                </Button>
                              ) : (
                                <div
                                  style={{ height: 26 }}
                                  aria-hidden="true"
                                />
                              )}

                              <Button
                                block
                                size="small"
                                loading={isSaving}
                                disabled={locked}
                                type={claimed ? "default" : "primary"}
                                icon={
                                  equipped ? <CheckCircleOutlined /> : undefined
                                }
                                style={{
                                  height: 26,
                                  borderRadius: 12,
                                  fontWeight: 800,
                                  background: claimed
                                    ? "rgba(255,255,255,0.72)"
                                    : "var(--dashboard-yellow)",
                                  borderColor: claimed
                                    ? palette.border
                                    : "var(--dashboard-yellow-dark)",
                                  color: "var(--dashboard-header-blue)",
                                }}
                              >
                                {getRewardActionLabel(reward, locked, isSaving)}
                              </Button>
                            </div>
                          </Card>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    type="text"
                    shape="circle"
                    icon={<RightOutlined />}
                    onClick={() => scrollBattlePass(1)}
                    aria-label="Ver niveles siguientes"
                    style={{
                      flexShrink: 0,
                      color: dashboardPalette.headerBlue,
                      fontSize: 18,
                    }}
                  />
                </div>
              </Space>
            </Card>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} md={7}>
                <Card
                  className="app-glass"
                  style={{
                    height: "100%",
                    borderRadius: 24,
                    border: "1px solid var(--dashboard-panel-border)",
                    boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: "100%", alignItems: "center" }}
                  >
                    <Typography.Title
                      level={4}
                      style={{
                        margin: 0,
                        color: dashboardPalette.headerBlue,
                      }}
                    >
                      Tu perfil
                    </Typography.Title>
                    <RewardedProfileDisplay
                      src={userData?.PortraitImg}
                      alt="Perfil"
                      fallbackSrc={avatarImage}
                      width={160}
                      height={160}
                      border={`4px solid ${dashboardPalette.headerBlue}`}
                      boxShadow="0 8px 0 rgba(47,43,116,0.18)"
                      backgroundColor="#fff4c4"
                      rewards={[...mergedRewards, ...(userData?.Items ?? [])]}
                      overlayMode="full"
                      overlayImageSx={{ top: -14 }}
                      badgeSize={24}
                      overlaySize={32}
                    />
                    <Typography.Text
                      strong
                      style={{ color: dashboardPalette.headerBlue }}
                    >
                      {userData?.Nombre || "Usuario"}
                    </Typography.Text>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} md={17}>
                <Card
                  className="app-glass"
                  style={{
                    height: "100%",
                    borderRadius: 24,
                    border: "1px solid var(--dashboard-panel-border)",
                    boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
                  }}
                  bodyStyle={{ padding: 20 }}
                >
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: "100%" }}
                  >
                    <Typography.Title
                      level={4}
                      style={{
                        margin: 0,
                        color: dashboardPalette.headerBlue,
                      }}
                    >
                      Inventario
                    </Typography.Title>
                    {inventoryItems.length ? (
                      <>
                        <Typography.Text
                          style={{ color: dashboardPalette.textDark }}
                        >
                          Tocá un ítem para equiparlo o quitarlo.
                        </Typography.Text>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(110px, 1fr))",
                            gap: 16,
                            width: "100%",
                          }}
                        >
                          {inventoryItems.map((reward) => {
                            const equipable = isRewardEquipable(reward);
                            const isSaving = savingRewardId === reward.id;
                            return (
                              <Tooltip
                                key={reward.id}
                                title={
                                  reward.description
                                    ? `${reward.name} — ${reward.description}`
                                    : reward.name
                                }
                              >
                                <button
                                  type="button"
                                  disabled={!equipable || isSaving}
                                  onClick={() =>
                                    equipable && handleRewardAction(reward)
                                  }
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    aspectRatio: "1 / 1",
                                    borderRadius: 18,
                                    padding: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: reward.equipped
                                      ? "rgba(79,194,134,0.18)"
                                      : "rgba(255,255,255,0.72)",
                                    border: reward.equipped
                                      ? "2px solid var(--dashboard-teal)"
                                      : `1px solid ${dashboardPalette.panelBorder}`,
                                    cursor: equipable ? "pointer" : "default",
                                  }}
                                >
                                  {renderRewardVisual(reward, 60)}
                                  {reward.equipped && (
                                    <span
                                      style={{
                                        position: "absolute",
                                        top: 6,
                                        right: 6,
                                        color: "var(--dashboard-teal)",
                                        fontSize: 16,
                                        lineHeight: 1,
                                      }}
                                    >
                                      <CheckCircleOutlined />
                                    </span>
                                  )}
                                </button>
                              </Tooltip>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <Typography.Text
                        style={{ color: dashboardPalette.textDark }}
                      >
                        Todavía no reclamaste recompensas. Tocá una de las
                        disponibles en el pase para agregarla acá.
                      </Typography.Text>
                    )}
                  </Space>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
      <RewardRedeemModal
        reward={redeemInfoReward}
        open={Boolean(redeemInfoReward)}
        onClose={() => setRedeemInfoReward(null)}
      />
    </DashboardLayout>
  );
};

export default Redeem;
